// RUTA: src/components/fitness/CardioRing.tsx
//
// Anillo circular de progreso estilo Apple Fitness para mostrar la
// adherencia diaria de cardio (kcal hechas / meta). Reutilizable —
// se usa en CardioGoalCard del dashboard del cliente y podría usarse
// en el panel del coach también.

import { CountUp } from '@/components/ui/CountUp';

interface CardioRingProps {
  /** Calorías quemadas hoy */
  current: number;
  /** Meta diaria de kcal definida por el coach */
  goal: number;
  /** Tamaño del anillo en píxeles. Por default 140. */
  size?: number;
  /** Grosor del trazo. Por default 12. */
  stroke?: number;
}

export function CardioRing({ current, goal, size = 140, stroke = 12 }: CardioRingProps) {
  const safeGoal = goal > 0 ? goal : 1; // evita divide-by-zero
  const pct = Math.min(100, (current / safeGoal) * 100);
  const cumplido = current >= goal && goal > 0;

  const radius = (size - stroke) / 2;
  const circumference = 2 * Math.PI * radius;
  const dashOffset = circumference - (pct / 100) * circumference;
  const center = size / 2;

  // Color del progreso: verde si cumplió, accent si no.
  const progressColor = cumplido ? '#22c55e' : '#dc2626'; // green-500 / accent (red-600)
  const trackColor = 'rgba(255,255,255,0.10)';

  // Anillo "respira" cuando ya cumpliste meta — refuerzo emocional.
  const breathClass = cumplido ? 'animate-breath' : '';

  return (
    <div className={`relative inline-flex items-center justify-center ${breathClass}`} style={{ width: size, height: size }}>
      <svg width={size} height={size} className="-rotate-90">
        {/* Track */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={trackColor}
          strokeWidth={stroke}
        />
        {/* Progress */}
        <circle
          cx={center}
          cy={center}
          r={radius}
          fill="none"
          stroke={progressColor}
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circumference}
          strokeDashoffset={dashOffset}
          style={{ transition: 'stroke-dashoffset 600ms ease-out' }}
        />
      </svg>
      {/* Center label */}
      <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-2">
        <span className="text-[10px] font-bold uppercase tracking-wider text-primary-foreground/60">
          Hoy
        </span>
        <CountUp
          value={current}
          className="text-2xl font-black text-primary-foreground leading-none mt-0.5"
        />
        <span className="text-[10px] font-bold text-primary-foreground/60 mt-0.5">
          / {goal.toLocaleString()} kcal
        </span>
        <span className={`text-[11px] font-black mt-1 ${cumplido ? 'text-green-400' : 'text-accent'}`}>
          {Math.round(pct)}%
        </span>
      </div>
    </div>
  );
}
